import React, { useEffect, useRef } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import OSM from "ol/source/OSM";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { fromLonLat } from "ol/proj";
import { Style, Circle, Fill, Stroke } from "ol/style";
import MouseWheelZoom from "ol/interaction/MouseWheelZoom";
import { defaults as defaultInteractions } from "ol/interaction";
import Collection from "ol/Collection";

const LocationMap = ({ location, height = "300px" }) => {
  const mapRef = useRef();
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    // Initialize map if it doesn't exist
    if (!mapInstanceRef.current && mapRef.current) {
      // Create vector source and layer for the location marker
      const vectorSource = new VectorSource();
      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: new Style({
          image: new Circle({
            radius: 8,
            fill: new Fill({
              color: "#3399CC",
            }),
            stroke: new Stroke({
              color: "#fff",
              width: 2,
            }),
          }),
        }),
      });

      // Create custom interactions - get all defaults except mouseWheelZoom
      const interactions = defaultInteractions({
        mouseWheelZoom: false,
      });

      // Add custom mouseWheelZoom that only works with Ctrl key
      const mouseWheelZoom = new MouseWheelZoom({
        condition: (mapBrowserEvent) => {
          return (
            mapBrowserEvent.originalEvent.ctrlKey ||
            mapBrowserEvent.originalEvent.metaKey
          ); // For Mac compatibility
        },
      });

      // Add the custom mouseWheelZoom to the interactions
      interactions.push(mouseWheelZoom);

      // Create map instance with OSM base layer
      mapInstanceRef.current = new Map({
        target: mapRef.current,
        layers: [
          new TileLayer({
            source: new OSM(),
          }),
          vectorLayer,
        ],
        view: new View({
          center: fromLonLat([31.2357, 30.0444]), // Default to Cairo
          zoom: 12,
        }),
        interactions: interactions,
      });

      // Add a help text overlay to inform users how to zoom
      const helpText = document.createElement("div");
      helpText.className = "map-help-text";
      helpText.innerHTML = "Hold Ctrl/⌘ key + scroll to zoom map";
      helpText.style.position = "absolute";
      helpText.style.bottom = "10px";
      helpText.style.left = "10px";
      helpText.style.backgroundColor = "rgba(255,255,255,0.7)";
      helpText.style.padding = "5px 10px";
      helpText.style.borderRadius = "4px";
      helpText.style.fontSize = "12px";
      helpText.style.zIndex = "1000";
      mapRef.current.appendChild(helpText);
    }

    // Update marker position when location changes
    if (mapInstanceRef.current && location) {
      const map = mapInstanceRef.current;
      const vectorSource = map.getLayers().getArray()[1].getSource();

      // Clear existing features
      vectorSource.clear();

      // Create a feature at the location
      const feature = new Feature({
        geometry: new Point(
          fromLonLat([location.longitude, location.latitude])
        ),
      });

      // Add the feature to the vector source
      vectorSource.addFeature(feature);

      // Center the map on the location
      map.getView().animate({
        center: fromLonLat([location.longitude, location.latitude]),
        zoom: 16,
        duration: 1000,
      });
    }

    return () => {
      // Clean up map instance on unmount
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setTarget(undefined);
        mapInstanceRef.current = null;
      }
    };
  }, [location]);

  return (
    <div
      ref={mapRef}
      style={{
        width: "100%",
        height,
        border: "1px solid #ddd",
        borderRadius: "4px",
        position: "relative", // Important for positioning the help text
      }}
    />
  );
};

export default LocationMap;
